import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { useMobileLayout } from '../../utils/useMobileLayout';

// Payment categories — mirror backend PaymentCategory. UNPAID is derived
// (a year with no settling payment), so it isn't an option here.
export const PAYMENT_CATEGORIES = [
  { value: 'PAID', label: 'Paid (full)' },
  { value: 'PARTIAL', label: 'Partial payment' },
  { value: 'SHORT', label: 'Short payment (underpaid)' },
  { value: 'DONATION', label: 'Donation (extra)' },
];

const PaymentDialog = ({ open, onClose, onSave, year, payment }) => {
  const { isMobile } = useMobileLayout();
  const [formData, setFormData] = useState({
    year: year || '',
    category: 'PAID',
    amount: '',
    paid_date: '',
    payment_ref: '',
    notes: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (payment) {
      setFormData({
        year: payment.year || year || '',
        category: payment.category || (payment.is_paid ? 'PAID' : 'PARTIAL'),
        amount: payment.amount != null ? String(payment.amount) : '',
        paid_date: payment.paid_date ? payment.paid_date.substring(0, 10) : '',
        payment_ref: payment.payment_ref || '',
        notes: payment.notes || '',
      });
    } else {
      setFormData({
        year: year || '',
        category: 'PAID',
        amount: '',
        paid_date: new Date().toISOString().substring(0, 10),
        payment_ref: '',
        notes: '',
      });
    }
  }, [payment, year, open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // is_paid stays meaningful for legacy consumers / the list chip: only a
      // full PAID payment counts as "paid".
      await onSave({
        year: formData.year,
        category: formData.category,
        amount: formData.amount === '' ? null : Number(formData.amount),
        is_paid: formData.category === 'PAID',
        paid_date: formData.paid_date || null,
        payment_ref: formData.payment_ref || null,
        notes: formData.notes || null,
      });
      onClose();
    } catch (error) {
      console.error('Error saving payment:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>{payment ? 'Edit Payment' : 'Record Payment'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Year"
            type="number"
            value={formData.year}
            onChange={(e) => handleChange('year', e.target.value ? Number(e.target.value) : '')}
            fullWidth
            inputProps={{ min: 2000, max: 2099 }}
            InputProps={{ readOnly: Boolean(payment) }}
            helperText={
              payment
                ? undefined
                : 'You can record more than one payment in a year (e.g. an instalment or a donation).'
            }
          />
          <TextField
            select
            label="Category"
            value={formData.category}
            onChange={(e) => handleChange('category', e.target.value)}
            fullWidth
          >
            {PAYMENT_CATEGORIES.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Amount"
            type="number"
            value={formData.amount}
            onChange={(e) => handleChange('amount', e.target.value)}
            fullWidth
            inputProps={{ min: 0, step: '0.01' }}
            InputProps={{
              startAdornment: <InputAdornment position="start">R</InputAdornment>,
            }}
          />
          <TextField
            label="Payment Date"
            type="date"
            value={formData.paid_date}
            onChange={(e) => handleChange('paid_date', e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Payment Reference"
            value={formData.payment_ref}
            onChange={(e) => handleChange('payment_ref', e.target.value)}
            fullWidth
          />
          <TextField
            label="Notes"
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={isSaving}
        >
          {isSaving ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDialog;
